import React, { lazy, Suspense, useState } from "react";
import { Route, Routes } from "react-router-dom";
import { useUser } from "@/shared/lib/hooks/useUser/useUser";
import { useAdminUI } from "@/shared/config/admin";

/*
 * /:lang/account/gifts — «Мои подарки», пока только для администраторов
 * (подарки покупают пока только они).
 *
 * До страницы этот адрес ловил общий маршрут «*» (NOT_FOUND) и отвечал голым
 * «404» всем. Так и остаётся для всех, кроме администраторов, — поэтому маршрут
 * плоский и решает сам, как LeagueUsersRoute:
 *   не вошёл                                        → «404»;
 *   вошёл, не администратор или «как обычный»       → «404»;
 *   администратор                                   → каркас кабинета + «Мои подарки».
 * Одно исключение: администратор в режиме «как обычный пользователь», которого
 * Stripe вернул сюда после оплаты подарка (?session_id=), видит свою ссылку —
 * иначе оплаченный подарок потерялся бы за «404».
 */

const AccountPage = lazy(() => import("@/pages/Account").then((m) => ({ default: m.default })));
const MyGiftsPage = lazy(() => import("@/pages/MyGiftsPage").then((m) => ({ default: m.MyGiftsPage })));

/** Ровно то, что рисует NOT_FOUND в routeConfig. */
const NotFound = () => <div>404</div>;

const cameFromCheckout = (): boolean => {
    try {
        return !!new URLSearchParams(window.location.search).get("session_id");
    } catch {
        return false;
    }
};

export const MyGiftsRoute = () => {
    const { user } = useUser();
    const email = user && typeof user !== "string" ? user.email || "" : "";
    if (!email) return <NotFound />;
    return <AdminOnly email={email} />;
};

const AdminOnly = ({ email }: { email: string }) => {
    const { isAdmin, isRealAdmin, loading } = useAdminUI(email);
    // Решается один раз: страница сама убирает session_id из адреса.
    const [fromCheckout] = useState(cameFromCheckout);
    if (loading) return <div data-testid="my-gifts-checking" />;
    if (!isAdmin && !(isRealAdmin && fromCheckout)) return <NotFound />;
    return (
        <Routes>
            <Route
                element={
                    <Suspense fallback="">
                        <AccountPage />
                    </Suspense>
                }
            >
                <Route
                    path="*"
                    element={
                        <Suspense fallback="">
                            <MyGiftsPage />
                        </Suspense>
                    }
                />
            </Route>
        </Routes>
    );
};

export default MyGiftsRoute;
